import type { ReactNode } from "react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from "@mui/material"

import type { AlertSettingAccordion, AlertSettingParam } from "types/alerts.types"

import { getUiConfigValue } from "helpers/formatters/alertSettingsFormatters"
import useEntityAlertTypes from "helpers/hooks/useEntityAlertTypes"
import { getAlertFields } from "helpers/utils/alerts"
import { getTelemetryTranslation } from "helpers/utils/translations"
import Spinner from "widgets/common/Spinner"
import {
  getConfigurationParams,
  getThresholdDescription,
  getThresholdsUnit,
} from "./helpers"

interface AlertConfigurationProps {
  alert: AlertSettingAccordion
}

// On small screens the label goes above the value, so the value gets the full width.
const ConfigRow = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <Stack direction={{ xs: "column", sm: "row" }} gap={{ xs: 0.25, sm: 2 }}>
    <Typography
      component="div"
      variant="body2"
      color="text.secondary"
      sx={{ width: { sm: "10rem" }, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      sx={{ minWidth: 0, overflowWrap: "anywhere" }}
    >
      {value}
    </Typography>
  </Stack>
)

const ConfigSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack gap={{ xs: 1.5, sm: 1 }}>
    <Typography variant="subtitle2" fontWeight={700}>
      {title}
    </Typography>
    {children}
  </Stack>
)

const AlertConfiguration = ({ alert }: AlertConfigurationProps) => {
  const theme = useTheme()
  const isWide = useMediaQuery(theme.breakpoints.up("md"))
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage || "en"
  const { alertTypes, isLoading, isError } = useEntityAlertTypes(alert.entity)

  const {
    thresholdsParam,
    fieldsParam,
    conditionParams,
    resolutionParams,
    notificationParams,
    noteParam,
  } = useMemo(
    () => getConfigurationParams(alert.type, alertTypes ?? []),
    [alert.type, alertTypes],
  )
  const note = String(alert.config.note ?? "").trim()
  const thresholds = alert.config.thresholds ?? []
  const fields = alert.config.fields ?? []
  const unit = useMemo(() => getThresholdsUnit(getAlertFields(alert)), [alert])
  const hasThresholds = Boolean(thresholdsParam) && thresholds.length > 0
  const hasFields = Boolean(fieldsParam) && fields.length > 0
  const hasNote = Boolean(noteParam) && note.length > 0
  const hasConditionColumn =
    hasThresholds ||
    hasFields ||
    conditionParams.length > 0 ||
    resolutionParams.length > 0
  const hasNotificationColumn = notificationParams.length > 0 || hasNote
  const targetColors: Record<string, string> = {
    alert: theme.palette.error.main,
    ok: theme.palette.secondary.main,
  }
  const renderRows = (params: AlertSettingParam[]) =>
    params.map((param) => (
      <ConfigRow
        key={param.id}
        label={param.name}
        value={getUiConfigValue(param, alert, locale)}
      />
    ))

  if (isLoading) return <Spinner />
  if (isError || (!hasConditionColumn && !hasNotificationColumn)) {
    return (
      <Typography
        variant="body2"
        sx={{ color: isError ? "error.main" : "text.secondary" }}
      >
        {t(isError ? "generic.ERROR" : "table.NO_DATA")}
      </Typography>
    )
  }
  return (
    <Stack
      direction={isWide ? "row" : "column"}
      gap={3}
      divider={<Divider orientation={isWide ? "vertical" : "horizontal"} flexItem />}
    >
      {hasConditionColumn && (
        <Stack gap={2.5} flex={1} minWidth={0}>
          {hasThresholds && thresholdsParam && (
            <ConfigSection title={thresholdsParam.name}>
              {thresholds.map((threshold, index) => (
                <ConfigRow
                  key={index}
                  label={
                    <Stack direction="row" alignItems="center" gap={0.75}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: targetColors[threshold.target] ?? "text.secondary",
                        }}
                      />
                      {t(`alert_settings.TARGET_${threshold.target.toUpperCase()}`, {
                        defaultValue: threshold.target,
                      })}
                    </Stack>
                  }
                  value={getThresholdDescription(threshold, t, locale, unit)}
                />
              ))}
            </ConfigSection>
          )}
          {hasFields && fieldsParam && (
            <ConfigSection title={fieldsParam.name}>
              {fields.map((field) => (
                <Typography
                  key={field}
                  variant="body2"
                  fontWeight={600}
                  sx={{ overflowWrap: "anywhere" }}
                >
                  {getTelemetryTranslation(field, t)}{" "}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({field})
                  </Typography>
                </Typography>
              ))}
            </ConfigSection>
          )}
          {conditionParams.length > 0 && (
            <ConfigSection title={t("alerts.CONDITION")}>
              {renderRows(conditionParams)}
            </ConfigSection>
          )}
          {resolutionParams.length > 0 && (
            <ConfigSection title={t("alerts.RESOLUTION")}>
              {renderRows(resolutionParams)}
            </ConfigSection>
          )}
        </Stack>
      )}
      {hasNotificationColumn && (
        <Stack gap={2.5} flex={1} minWidth={0}>
          {notificationParams.length > 0 && (
            <ConfigSection title={t("alerts.NOTIFICATION")}>
              {renderRows(notificationParams)}
            </ConfigSection>
          )}
          {hasNote && noteParam && (
            <ConfigSection title={noteParam.name}>
              <Typography
                variant="body2"
                sx={{ overflowWrap: "anywhere", whiteSpace: "pre-line" }}
              >
                {note}
              </Typography>
            </ConfigSection>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default AlertConfiguration
